import Section from '@packages/sections/section';
import * as focusTrap from "focus-trap";
import { slideInFromRight, slideOutToRight } from '@/helpers/jqry-replace';
import { disableBodyScroll, enableBodyScroll } from '@/helpers/bodyScrollLockWrapper';

const selectors = {
    openButton: '[data-mm-open]',
    closeButton: '[data-mm-close]',
    main: '[data-mm-main]',
    submenuOpenButton: '[data-mm-submenu-open]',
    submenuCloseButton: '[data-mm-submenu-close]',
    submenu: '[data-mm-submenu]',
    allowScroll: '[data-allow-scroll]',
    smartbannerBlock: '[data-mm-app-banner]',
    smartbanner: '.smartbanner',
    smartbannerExit: '.js_smartbanner__exit'
};

const classes = {
    lockScroll: 'lock-scroll'
};

class MenuMobileSection extends Section {
    async onLoad() {
        await super.onLoad();

        const {container} = this;
        if (!container) {
            return;
        }

        this.loading = false;
        this.openSubmenus = [];

        this.trap = focusTrap.createFocusTrap(container);

        this.addListeners();
        this.initSmartbanner();
    }

    initSmartbanner() {
        const {container} = this;
        if (!container) {
            return;
        }

        const smartbannerBlock = container.querySelector(selectors.smartbannerBlock);
        if (!smartbannerBlock) {
            return;
        }

        const src = smartbannerBlock.dataset.mmAppBanner;
        if (!src) {
            return;
        }

        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = src;

        script.onload = () => {
            window.addEventListener('load', () => {
                const {smartbanner} = window;
                
                if (!smartbanner) {
                    return;
                }

                smartbanner.publish();

                // const el = document.querySelector(selectors.smartbanner);
                // if (!el) {
                //     return;
                // }

                // smartbannerBlock.appendChild(el);

                // el.classList.add('smartbanner--loaded');
            });
        };
    
        document.head.appendChild(script);
    }

    addListeners() {
        this.addOpenListeners();
        this.addCloseListeners();
        this.addSubmenuOpenListeners();
        this.closeSubmenuOpenListeners();
    }

    addSubmenuOpenListeners() {
        const {container} = this;
        if (!container) {
            return;
        }

        const buttons = container.querySelectorAll(selectors.submenuOpenButton);
        if (!buttons?.length) {
            return;
        }

        buttons.forEach((button) => {
            button.addEventListener('click', (event) => {
                event.preventDefault();

                if (this.loading) {
                    return;
                }

                const id = button.getAttribute('aria-controls');
                if (!id) {
                    return;
                }

                const submenu = document.getElementById(id);
                if (!submenu) {
                    return;
                }

                this.loading = true;
            
                const lockScrollEls = container.querySelectorAll(`${selectors.allowScroll}:not(#${id})`);
                if (lockScrollEls?.length) {
                    lockScrollEls.forEach((el) => {
                        el.scrollTop = 0;
                        el.classList.add(classes.lockScroll);
                    });
                }

                submenu.classList.remove(classes.lockScroll);

                this.openSubmenus.push(submenu);

                button.setAttribute('aria-expanded', true);

                const closeButtons = submenu.querySelectorAll(`${selectors.submenuCloseButton}[aria-controls="${id}"]`);
                if (closeButtons?.length) {
                    closeButtons.forEach((closeButton) => {
                        closeButton.setAttribute('aria-expanded', true);
                    });
                }

                slideInFromRight(submenu, 300, () => {
                    this.loading = false;
                });
            });
        });
    }

    closeSubmenuOpenListeners() {
        const {container} = this;
        if (!container) {
            return;
        }

        const buttons = container.querySelectorAll(selectors.submenuCloseButton);
        if (!buttons?.length) {
            return;
        }

        buttons.forEach((button) => {
            button.addEventListener('click', (event) => {
                event.preventDefault();

                if (this.loading) {
                    return;
                }

                const id = button.getAttribute('aria-controls');
                if (!id) {
                    return;
                }

                const submenu = document.getElementById(id);
                if (!submenu) {
                    return;
                }

                this.loading = true;

                this.openSubmenus.pop();

                const closestAllowScrollEl = this.openSubmenus.at(-1) || submenu.parentElement?.closest(selectors.allowScroll);

                if (closestAllowScrollEl) {
                    closestAllowScrollEl.classList.remove(classes.lockScroll);
                }

                button.setAttribute('aria-expanded', false);

                const openButtons = container.querySelectorAll(`${selectors.submenuOpenButton}[aria-controls="${id}"]`);
                if (openButtons?.length) {
                    openButtons.forEach((openButton) => {
                        openButton.setAttribute('aria-expanded', false);
                    });
                }

                slideOutToRight(submenu, 300, null, () => {
                    this.loading = false;

                    submenu.scrollTop = 0;
                });
            });
        });
    }

    addOpenListeners() {
        const {container} = this;
        if (!container) {
            return;
        }

        const main = container.querySelector(selectors.main);
        if (!main) {
            return;
        }

        const buttons = document.querySelectorAll(selectors.openButton);
        if (!buttons?.length) {
            return;
        }

        const closeButtons = container.querySelectorAll(selectors.closeButton);

        buttons.forEach((button) => {
            button.addEventListener('click', (event) => {
                event.preventDefault();

                if (this.loading) {
                    return;
                }

                this.loading = true;
                
                container.style.display = 'block';
                slideInFromRight(main, 300, () => {
                    this.loading = false;
                });

                this.trap.activate();

                button.setAttribute('aria-expanded', true);

                if (closeButtons?.length) {
                    closeButtons.forEach((closeButton) => {
                        closeButton.setAttribute('aria-expanded', true);
                    });
                }

                disableBodyScroll(container, {
                    allowTouchMove(el) {
                        while (el && el !== document?.body) {
                            if (el.getAttribute('data-allow-scroll') !== null) {
                                return true;
                            }

                            el = el?.parentElement;
                        }

                        return false;
                    },
                });
            });
        });
    }

    addCloseListeners() {
        const {container} = this;
        if (!container) {
            return;
        }

        const main = container.querySelector(selectors.main);
        if (!main) {
            return;
        }

        const submenus = container.querySelectorAll(selectors.submenu);
        const allowScrollEls = container.querySelectorAll(selectors.allowScroll);
        const buttons = [
            ...document.querySelectorAll(selectors.openButton),
            ...container.querySelectorAll(selectors.closeButton),
            ...container.querySelectorAll(selectors.submenuOpenButton),
            ...container.querySelectorAll(selectors.submenuCloseButton)
        ];

        container.addEventListener('click', (event) => {
            const smartBannerExit = !!event.target.closest(selectors.smartbannerExit);
            if (smartBannerExit) {
                event.preventDefault();
                return;
            }

            const mainEl = !!event.target.closest(selectors.main);
            const closeButton = !!event.target.closest(selectors.closeButton);

            if ((mainEl && !closeButton) || this.loading) {
                return;
            }

            this.loading = true;

            slideOutToRight(main, 300, container, () => {
                this.loading = false;

                if (submenus?.length) {
                    submenus.forEach((el) => {
                        el.style.transform = 'translateX(100%)';
                        el.style.display = 'none';
                    });
                }

                this.openSubmenus = [];

                if (allowScrollEls?.length) {
                    allowScrollEls.forEach((el) => {
                        el.classList.remove(classes.lockScroll);
                    });
                }
            });

            if (buttons?.length) {
                buttons.forEach((button) => {
                    button.setAttribute('aria-expanded', false);
                });
            }

            this.trap.deactivate();

            enableBodyScroll(container);
        });
    }
}

export default MenuMobileSection;
